/*
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2019, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./api/EventGroupsAPI',
	'./EventGroupsAPISpec',
	'../../../lib/@waca/dashboard-common/dist/utils/ContentUtil'
], function (APIFactory,
	EventGroupsAPI,
	EventGroupsAPISpec,
	ContentUtil) {

	class EventGroups extends EventGroupsAPISpec {
		constructor({ features }) {
			super();

			this.features = features;
			this.canvas = features['Canvas'];

			this._api = APIFactory.createAPI(this, [EventGroupsAPI]);

			this.features['UndoRedo.internal'].registerAPIEvents(this._api);

			this.canvas.on('move:content', this.onMoveContent, this);
		}

		getAPI() {
			return this._api;
		}

		onMoveContent(payload) {
			const newContainerId = payload.info.containerId;
			const previousContainerId = payload.info.previousContainerId;
			if (newContainerId !== previousContainerId) {
				// contents are moved to a different container
				const pageId = ContentUtil.getPageContent(this.canvas.getContent(newContainerId)).getId();
				const defaultEventGroupId = this.createDefaultGroupId(pageId);
				const widgetIds = [];
				payload.info.contentIdList.forEach(id => {
					if (this.getGroupId(id)) {
						// only consider content that supports eventGroup (static widget should not be considered)
						widgetIds.push(id);
					}
				});
				if (widgetIds.length) {
					this.addToGroup(defaultEventGroupId, widgetIds, payload.transactionToken);
				}
			}
		}

		destroy() {
			if (this.canvas) {
				this.canvas.off('move:content', this.onMoveContent, this);
			}
			this._api = null;
		}

		groupExists(groupId) {
			const boardModel = this._getBoardModel();
			const eventGroups = boardModel.eventGroups;
			return !!eventGroups.get(groupId);
		}

		getGroupId(widgetId) {
			const boardModel = this._getBoardModel();
			const eventGroups = boardModel.eventGroups;
			const group = eventGroups.findGroup(widgetId);
			if (group) {
				return group.getId();
			}

			return null;
		}

		getDefaultGroupId(content) {
			let pageContent = content;
			while (pageContent.getType() !== 'page' && pageContent.getContainer()) {
				pageContent = pageContent.getContainer();
			}
			return ((pageContent.getType() === 'page' && this.createDefaultGroupId(pageContent.getId())) || null);
		}

		getGroupIdsOnPage(pageId) {
			const boardModel = this._getBoardModel();
			const eventGroups = boardModel.eventGroups;
			const groups = eventGroups.getGroupsOnPage(pageId);
			let groupIds = [];
			if (groups && groups.length) {
				groups.forEach(group => {
					groupIds.push(group.id);
				});
			}
			return groupIds;
		}

		getContentIdList(groupId) {
			const boardModel = this._getBoardModel();
			const eventGroups = boardModel.eventGroups;
			const group = eventGroups.get(groupId);
			if (group && group.widgetIds) {
				return JSON.parse(JSON.stringify(group.widgetIds));
			}
		}

		addToGroup(groupId, widgetIds, transactionToken) {
			const boardModel = this._getBoardModel();
			const eventGroups = boardModel.eventGroups;
			const options = {
				payloadData: {
					skipUndoRedo: true,
					undoRedoTransactionId: transactionToken.transactionId,
					transactionToken: transactionToken
				}
			};
			if (this.groupExists(groupId)) {
				eventGroups.addToGroup(groupId, widgetIds, options);
			} else {
				// create requires only the id, not the event group id
				options.targetPageId = groupId.slice(0, groupId.indexOf(':'));
				eventGroups.createGroup(widgetIds, options);
			}
		}

		createGroup(widgetIds, transactionToken) {
			const boardModel = this._getBoardModel();
			const eventGroups = boardModel.eventGroups;
			const options = {
				payloadData: {
					skipUndoRedo: true,
					undoRedoTransactionId: transactionToken.transactionId,
					transactionToken: transactionToken
				}
			};
			const groupId = eventGroups.createGroup(widgetIds, options);
			return groupId;
		}

		removeFromGroup(widgetIds, transactionToken) {
			const boardModel = this._getBoardModel();
			const eventGroups = boardModel.eventGroups;
			const options = {
				payloadData: {
					skipUndoRedo: true,
					undoRedoTransactionId: transactionToken.transactionId,
					transactionToken: transactionToken
				}
			};
			const groupIds = eventGroups.disconnectFromGroup(widgetIds, options);
			return groupIds;
		}

		_getBoardModel() {
			const internal = this.features['internal'];
			return internal.getBoardModel();
		}

		createDefaultGroupId(pageId) {
			const boardModel = this._getBoardModel();
			const eventGroups = boardModel.eventGroups;
			return eventGroups.createDefaultGroupId(pageId);
		}
	}


	return EventGroups;
});
